import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { MentorMetricsPeriod } from "@/lib/api/mentor-metrics-dashboard";
import { useLeadMentorFilterOptions } from "@/lib/hooks/mentor-performance/use-lead-mentor-filter-options";
import { useDebounce } from "@/lib/hooks/use-debounce";
import { useMentorPerformanceEvent } from "@/lib/hooks/mentor-performance/use-mentor-performance-event";
import { PostHogEvents } from "@/lib/posthog-events";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";

interface MentorPerformanceControlsProps {
  period: MentorMetricsPeriod;
  availableMonths: string[];
  leadMentorIds: string[];
  search: string;
  showLeadMentorFilter: boolean;
  onPeriodChange: (period: MentorMetricsPeriod) => void;
  onLeadMentorIdsChange: (ids: string[]) => void;
  onSearchChange: (search: string) => void;
}

export function MentorPerformanceControls({
  period,
  availableMonths,
  leadMentorIds,
  search,
  showLeadMentorFilter,
  onPeriodChange,
  onLeadMentorIdsChange,
  onSearchChange,
}: MentorPerformanceControlsProps) {
  const { track } = useMentorPerformanceEvent();
  const { options: leadMentorOptions, isPending: isLeadMentorsLoading } =
    useLeadMentorFilterOptions(showLeadMentorFilter);
  const [searchInput, setSearchInput] = useState(search);
  const debouncedSearch = useDebounce(searchInput, 500);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    if (debouncedSearch !== search) {
      onSearchChange(debouncedSearch);
    }
  }, [debouncedSearch, onSearchChange, search]);

  const selectedLeadMentors = leadMentorOptions.filter((option) =>
    leadMentorIds.includes(option.value),
  );
  const visibleMonths = availableMonths.includes(period)
    ? availableMonths
    : [period, ...availableMonths];

  return (
    <div className="flex w-full flex-col justify-between gap-3 md:flex-row md:items-center">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <Select
          value={period}
          onValueChange={(value: MentorMetricsPeriod) => {
            onPeriodChange(value);
            track(PostHogEvents.MENTOR_DASHBOARD_TIME_PERIOD_CHANGED, {
              time_period: value,
            });
          }}
        >
          <SelectTrigger className="h-14 w-full rounded-md border-buttonBorder-tertiary bg-surfaces-z0 px-5 text-body-1 text-text-tertiary shadow-none sm:w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {visibleMonths.map((month) => (
              <SelectItem key={month} value={month}>
                {DateTime.fromFormat(month, "yyyy-MM").toFormat("LLLL yyyy")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {showLeadMentorFilter &&
          (isLeadMentorsLoading ? (
            <Skeleton
              aria-label="Loading lead mentors"
              className="h-14 w-full rounded-md sm:w-[240px]"
            />
          ) : (
            <MultiSelect
              options={leadMentorOptions}
              value={selectedLeadMentors}
              placeholder="All lead mentors"
              maxVisibleBadges={2}
              className="min-h-14 w-full rounded-md border-buttonBorder-tertiary bg-surfaces-z0 px-4 py-2 text-body-1 text-text-tertiary shadow-none hover:bg-surfaces-z0 sm:w-[240px]"
              onChange={(options) => {
                const nextIds = options.map((option) => option.value);
                const action =
                  nextIds.length > leadMentorIds.length ? "added" : "removed";
                onLeadMentorIdsChange(nextIds);
                track(
                  PostHogEvents.MENTOR_DASHBOARD_LEAD_MENTOR_FILTER_CHANGED,
                  {
                    selected_lead_mentor_ids: nextIds,
                    action,
                  },
                );
              }}
            />
          ))}
      </div>

      <div className="w-full md:w-[322px]">
        <Input
          aria-label="Search mentors"
          placeholder="Search"
          value={searchInput}
          onFocus={() => track(PostHogEvents.MENTOR_DASHBOARD_SEARCH_USED)}
          onChange={(event) => setSearchInput(event.target.value)}
          startIcon={
            <MagnifyingGlassIcon className="ml-3 size-5 text-text-tertiary" />
          }
          className="h-14 rounded-md border-buttonBorder-tertiary bg-surfaces-z0 pl-[52px] text-body-1 shadow-none placeholder:text-text-tertiary"
        />
      </div>
    </div>
  );
}
